import { useState, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Upload, X, Image as ImageIcon } from "lucide-react";

const ALLOWED_TYPES = ["image/jpeg", "image/png", "image/webp"];
const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2MB

export interface ImageData {
  src: string;
  alt: string;
  width: number;
  height: number;
}

interface ImageUploaderProps {
  label: string;
  value: ImageData | null;
  onChange: (image: ImageData | null) => void;
  storagePath: string; // e.g. "blog" or "case-studies"
  /** Show validation errors */
  showErrors?: boolean;
}

const ImageUploader = ({
  label,
  value,
  onChange,
  storagePath,
  showErrors = false,
}: ImageUploaderProps) => {
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate type
    if (!ALLOWED_TYPES.includes(file.type)) {
      toast.error("Only JPG, PNG, and WebP images are allowed");
      return;
    }

    // Validate size
    if (file.size > MAX_FILE_SIZE) {
      toast.error("Image must be under 2MB");
      return;
    }

    // Get dimensions
    const dimensions = await getImageDimensions(file);

    setUploading(true);
    try {
      const ext = file.name.split(".").pop() || "jpg";
      const fileName = `${storagePath}/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;

      const { error } = await supabase.storage
        .from("site-assets")
        .upload(fileName, file, { cacheControl: "31536000", upsert: false });

      if (error) throw error;

      const { data: urlData } = supabase.storage
        .from("site-assets")
        .getPublicUrl(fileName);

      onChange({
        src: urlData.publicUrl,
        alt: value?.alt || "",
        width: dimensions.width,
        height: dimensions.height,
      });

      toast.success("Image uploaded");
    } catch (err: any) {
      toast.error(err.message || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const getImageDimensions = (file: File): Promise<{ width: number; height: number }> => {
    return new Promise((resolve) => {
      const img = new window.Image();
      img.onload = () => {
        resolve({ width: img.naturalWidth, height: img.naturalHeight });
        URL.revokeObjectURL(img.src);
      };
      img.onerror = () => resolve({ width: 0, height: 0 });
      img.src = URL.createObjectURL(file);
    });
  };

  const handleRemove = () => {
    onChange(null);
  };

  const handleAltChange = (alt: string) => {
    if (value) {
      onChange({ ...value, alt });
    }
  };

  return (
    <div className="space-y-3">
      <Label className="text-sm font-medium">{label}</Label>

      {value?.src ? (
        <div className="space-y-2">
          {/* Preview */}
          <div className="relative border border-border rounded-sm overflow-hidden bg-muted/30">
            <img
              src={value.src}
              alt={value.alt}
              className="w-full h-32 object-cover"
            />
            <Button
              type="button"
              variant="destructive"
              size="sm"
              className="absolute top-2 right-2 h-7 w-7 p-0"
              onClick={handleRemove}
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          </div>

          {/* Alt text */}
          <div className="space-y-1">
            <Label className="text-xs">
              Alt text{" "}
              {showErrors && !value.alt.trim() && (
                <span className="text-destructive">(required)</span>
              )}
            </Label>
            <Input
              value={value.alt}
              onChange={(e) => handleAltChange(e.target.value)}
              placeholder="Describe this image"
              className="text-sm"
            />
          </div>

          {/* Dimensions display */}
          <p className="text-[10px] text-muted-foreground">
            {value.width}×{value.height}px
          </p>
        </div>
      ) : (
        <div>
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
            onChange={handleUpload}
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="w-full justify-center gap-2 py-6 border-dashed"
          >
            {uploading ? (
              <span className="text-sm text-muted-foreground">Uploading...</span>
            ) : (
              <>
                <Upload className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Upload image</span>
              </>
            )}
          </Button>
          <p className="text-[10px] text-muted-foreground mt-1">
            JPG, PNG, or WebP. Max 2MB.
          </p>
        </div>
      )}
    </div>
  );
};

export default ImageUploader;
